import { cache } from "react";
import type { BlogPost, ClientLogo, FAQ, GalleryItem, Product, Service, SitePayload } from "./types";
import { getBackendUrl } from "./backend";

async function apiGet<T>(path: string, opts?: RequestInit & { next?: { revalidate?: number } }): Promise<T> {
  const url = `${getBackendUrl()}${path}`;
  const res = await fetch(url, {
    ...(opts?.next ? { next: opts.next } : { next: { revalidate: 60 } }),
    ...(opts?.cache ? { cache: opts.cache } : {}),
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`API ${path}: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const getSite = cache(async (): Promise<SitePayload> => {
  return apiGet<SitePayload>("/api/v1/site", { next: { revalidate: 60 } });
});

export const getGalleryItems = cache(async (): Promise<GalleryItem[]> => {
  const data = await apiGet<{ items: GalleryItem[] }>("/api/v1/gallery");
  return data.items ?? [];
});

export const getClientLogos = cache(async (): Promise<{
  clients: ClientLogo[];
  partners: ClientLogo[];
}> => {
  const data = await apiGet<{ clients: ClientLogo[]; partners: ClientLogo[] }>(
    "/api/v1/clients",
  );
  return {
    clients: data.clients ?? [],
    partners: data.partners ?? [],
  };
});

export const getFaqs = cache(async (): Promise<FAQ[]> => {
  const data = await apiGet<{ faqs: FAQ[] }>("/api/v1/faqs");
  return data.faqs ?? [];
});

export const getServices = cache(async (): Promise<Service[]> => {
  const data = await apiGet<{ services: Service[] }>("/api/v1/services");
  return data.services ?? [];
});

export async function getBlogs(q?: string, category?: string): Promise<BlogPost[]> {
  const sp = new URLSearchParams();
  if (q) sp.set("q", q);
  if (category) sp.set("category", category);
  const qs = sp.toString();
  const path = qs ? `/api/v1/blogs?${qs}` : "/api/v1/blogs";
  const data = await apiGet<{ blogs: BlogPost[] }>(path);
  return data.blogs ?? [];
}

export async function getBlogBySlug(slug: string): Promise<BlogPost | null> {
  try {
    const data = await apiGet<{ blog: BlogPost }>(
      `/api/v1/blog/${encodeURIComponent(slug)}`,
    );
    return data.blog ?? null;
  } catch {
    return null;
  }
}

export const getProducts = cache(async (): Promise<Product[]> => {
  const data = await apiGet<{ products: Product[] }>("/api/v1/products");
  return data.products ?? [];
});

export async function getProductBySlug(slug: string): Promise<Product | null> {
  try {
    const data = await apiGet<{ product: Product }>(
      `/api/v1/product/${encodeURIComponent(slug)}`,
    );
    return data.product ?? null;
  } catch {
    return null;
  }
}

// ── CMS Pages ────────────────────────────────────────────────────────────────

export type PageSection = {
  id: number
  kind: string
  position: number
  enabled: boolean
  data: Record<string, unknown>
  dataAr?: Record<string, unknown>
}

export type CmsPage = {
  id: number
  slug: string
  path: string
  title: string
  titleAr?: string
  nav_label?: string
  nav_labelAr?: string
  seo_title?: string
  seo_description?: string
  seo_keywords?: string
  og_image?: string
  sections: PageSection[]
}

export type NavPage = {
  id: number
  slug: string
  path: string
  title: string
  titleAr?: string
  nav_label?: string
  nav_labelAr?: string
  sort_order: number
  children: NavPage[]
}

export const getCmsPage = cache(async (path: string): Promise<CmsPage | null> => {
  try {
    return await apiGet<CmsPage>(`/api/v1/page?path=${encodeURIComponent(path)}`, {
      next: { revalidate: 60 },
    });
  } catch {
    return null;
  }
});

/** Draft/scheduled preview — requires token from admin Preview link. */
export async function getCmsPagePreview(
  path: string,
  token: string,
): Promise<CmsPage | null> {
  const trimmed = token.trim();
  if (!trimmed) return null;
  try {
    const url = `${getBackendUrl()}/api/v1/page/preview?path=${encodeURIComponent(path)}&token=${encodeURIComponent(trimmed)}`;
    const res = await fetch(url, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    return (await res.json()) as CmsPage;
  } catch {
    return null;
  }
}

export const getNavPages = cache(async (): Promise<NavPage[]> => {
  try {
    return await apiGet<NavPage[]>("/api/v1/pages");
  } catch {
    return [];
  }
});

// ── Footer Nav ────────────────────────────────────────────────────────────────

export type FooterNavItem = {
  id: number
  label: string
  labelAr?: string
  url: string
  target: string
  sort_order: number
  section: string
}

export type FooterNav = {
  header?: FooterNavItem[]
  footer?: FooterNavItem[]
  footer_legal?: FooterNavItem[]
}

export const getFooterNav = cache(async (): Promise<FooterNav> => {
  try {
    return await apiGet<FooterNav>("/api/v1/nav")
  } catch {
    return {}
  }
})

/** Site + navigation fetched once per request (deduped across layout, header, footer). */
export const getShellData = cache(async () => {
  const [site, navPages, footerNav] = await Promise.all([
    getSite(),
    getNavPages(),
    getFooterNav(),
  ]);
  return { site, navPages, footerNav };
});

// ── Site search ─────────────────────────────────────────────────────────────

export type SearchResult = {
  type: string
  title: string
  url: string
  excerpt?: string
}

export async function searchSite(q: string): Promise<SearchResult[]> {
  const trimmed = q.trim()
  if (trimmed.length < 2) return []
  try {
    const data = await apiGet<{ results: SearchResult[] }>(
      `/api/v1/search?q=${encodeURIComponent(trimmed)}`,
      { next: { revalidate: 60 } },
    )
    return data.results ?? []
  } catch {
    return []
  }
}
